/*
 * Copyright (c) 2023 FMR LLC.
 * All Rights Reserved.
 * Fidelity Confidential Information.
 */
export class Utils {
    /**
     * @description checks to see if the string has a decimal
     * @param text the text to check for a decimal
     * @returns true if the text has a decimal, false otherwise
     */
    public static hasDecimal(text: string): boolean {
        return text.indexOf('.') !== -1;
    }

    /**
     * @description checks to see if the decimal key was pressed
     * @param e the event to handle
     * @returns true if the decimal was pressed, false otherwise
     */
    public static isDecimalKeyPressed(e: any): boolean {
        return e.key === '.';
    }

    /**
     * @description checks to see if a number key is pressed
     * @param e the event to handle
     * @returns true if a number is pressed, false otherwise
     */
    public static isNumericKeyPressed(e: any): boolean {
        return e.key >= '0' && e.key <= '9';
    }

    /**
     * @description checks to see if a meta key is pressed
     * @param e the event to handle
     * @returns true if a meta key is pressed, false otherwise
     */
    public static isMetaKeyPressed(e: any): boolean {
        return e.metaKey;
    }

    /**
     * @description prevents more than one decimal being typed into an input
     * @param e the event to handle
     */
    public static handleInputDecimal(e: any): void {
        if (this.hasDecimal(e.target.value) && this.isDecimalKeyPressed(e)) {
            e.preventDefault();
        }
    }

    /**
     * @description checks for a numeric key, decimal, or a cmd/ctrl key as allowed inputs
     * @param e the event to handle
     */
    public static handleNumberInput(e: KeyboardEvent): void {
        if (
            !this.isNumericKeyPressed(e) &&
            !this.isDecimalKeyPressed(e) &&
            !this.isMetaKeyPressed(e)
        ) {
            e.preventDefault();
        }
    }

    /**
     * @description checks to see if the copied text is a valid input
     * @param e the event to handle
     * @returns true if the copied text is valid, false otherwise
     */
    public static isCopiedTextValid(e: any): boolean {
        return /^\d+(\.\d{1,})?$/.test(e.clipboardData.getData('text'));
    }

    /**
     * @description prevents pasting if the copied text is not valid
     * @param e the event to handle
     */
    public static handlePasteValid(e: any): void {
        if (!this.isCopiedTextValid(e)) {
            e.preventDefault();
        }
    }
}
